import { WarehouseMode, WarehouseNavigatedMessage, WarehousePresentation } from './host-bridge.messages';
import { IframeEmbedParams, IframeUrlState, urlStateToEmbedParams } from './iframe-url-state';
import { chainsEqual, EMPTY_CHAIN, RecordChain, rootRecordChanged } from './record-chain';

export type BridgeCommand =
	| { kind: 'none' }
	| { kind: 'set-mode'; mode: WarehouseMode }
	| { kind: 'open-record'; chain: RecordChain; mode: WarehouseMode | null; tab: number | null }
	| { kind: 'set-tab'; tab: number; mode: WarehouseMode | null }
	| { kind: 'reload'; params: IframeEmbedParams };

export function diffUrlToCommand(
	urlState: IframeUrlState,
	iframeState: IframeUrlState,
	presentation: WarehousePresentation,
): BridgeCommand {
	const modeChanged = urlState.mode !== null && iframeState.mode !== null && urlState.mode !== iframeState.mode;
	const nextMode = modeChanged ? urlState.mode : null;

	const chainChanged = !chainsEqual(urlState.chain, iframeState.chain);
	const tabChanged = urlState.tab !== null && urlState.tab !== iframeState.tab;
	const nextTab = tabChanged ? urlState.tab : null;

	if (urlState.page !== iframeState.page) {
		return { kind: 'reload', params: urlStateToEmbedParams(urlState, presentation) };
	}

	if (presentation === 'page' && rootRecordChanged(urlState.chain, iframeState.chain)) {
		return { kind: 'reload', params: urlStateToEmbedParams(urlState, presentation) };
	}
	if (chainChanged) return { kind: 'open-record', chain: urlState.chain, mode: nextMode, tab: nextTab };
	if (tabChanged) return { kind: 'set-tab', tab: urlState.tab as number, mode: nextMode };

	if (modeChanged) return { kind: 'set-mode', mode: urlState.mode as WarehouseMode };
	return { kind: 'none' };
}

export function applyNavigatedMessage(
	msg: WarehouseNavigatedMessage,
	prev: IframeUrlState | null,
	urlRid: string | null = null,
	surfacePresentation: WarehousePresentation = 'page',
): IframeUrlState {
	const presentation = msg.as ?? surfacePresentation;
	const isRecord = msg.view === 'record';
	const recordAsModal = isRecord && presentation === 'modal';

	return {
		chain: isRecord ? chainFromMessage(msg, prev, urlRid, recordAsModal) : EMPTY_CHAIN,
		page: msg.page ?? null,
		mode: msg.mode ?? (msg.view === 'dashboard' ? null : (prev?.mode ?? null)),
		tab: recordAsModal || msg.view !== 'record' ? (msg.tab ?? null) : null,
	};
}

function chainFromMessage(
	msg: WarehouseNavigatedMessage,
	prev: IframeUrlState | null,
	urlRid: string | null,
	recordAsModal: boolean,
): RecordChain {
	if (msg.chain !== undefined && msg.chain.length > 0) {
		return msg.chain.map((entry) => ({
			rid: String(entry.recordId),
			segment: entry.segment ?? null,
			tab: entry.tab ?? null,
		}));
	}
	const rid = msg.recordId ?? prev?.chain[0]?.rid ?? urlRid;
	if (rid === null || rid === undefined) return EMPTY_CHAIN;
	const tab = (recordAsModal ? msg.recordTab : msg.tab) ?? null;
	return [{ rid: String(rid), segment: prev?.chain[0]?.segment ?? null, tab }];
}
